import { DatePipe, Location } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { QueryParams } from "@ngrx/data";
import { Store } from "@ngrx/store";
import { combineLatest, Observable, Subscription } from "rxjs/index";
import { filter, map } from "rxjs/operators";
import { AuthenticationSelectors } from "../../auth/state/authentication.selectors";
import { Strategy } from "../../core/model/central/strategy.model";
import { User } from "../../core/model/user.model";
import { LocationsService } from "../../locations/state/locations.service";
import { AppointmentListSelectors } from "../appointment-list/state/appointment-list.selectors";
import { AppointmentListService } from "../appointment-list/state/appointment-list.service";
import { RemindersReportService } from "../appointment-list/state/reminders-report.service";
import { AppointmentSettingsActions } from "../appointment-settings/state/appointment-settings.actions";
import { StrategyService } from "../reminder-settings/state/strategy.service";
import { Appointment } from "./state/appointment.model";
import { AppointmentsActions } from "./state/appointments.actions";
import { DateAppointmentService } from "./state/date-appointment.service";
import { PreviewEditTemplateActions } from '../../preview-edit-template/state/preview-edit-template.actions';
import { AppointmentListActions } from '../appointment-list/state/appointment-list.actions';

@Component({
  selector: "appointments",
  templateUrl: "./appointments.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppointmentsComponent implements OnInit, OnDestroy {
  loading$: Observable<boolean>;
  locations$: Observable<Array<string>>;
  dateAppointments$: Observable<Array<Appointment>>;
  viewModel$: Observable<any>;
  pipe = new DatePipe("en-US");
  strategies$: Observable<Array<Strategy>>;
  subscriptions: Subscription = new Subscription();
  fromDate: Date = new Date();
  queryFilter$: Observable<any>;
  selectedRemoteAppointmentId: string;
  selectedAppointmentId: string;
  currentUser$: Observable<User>;
  firstRequest = true;
  @Input() readonly viewSessionId;

  constructor(
    private store: Store<any>,
    private activatedRoute: ActivatedRoute,
    private authenticationSelectors: AuthenticationSelectors,
    private strategyService: StrategyService,
    private appointmentListSelector: AppointmentListSelectors,
    private appointmentListService: AppointmentListService,
    private remindersReportService: RemindersReportService,
    private dateAppointmentService: DateAppointmentService,
    private locationsService: LocationsService,
    private location: Location
  ) {
    this.loading$ = this.dateAppointmentService.refreshing$;
    this.dateAppointments$ = this.dateAppointmentService.entities$;
    this.strategies$ = this.strategyService.entities$;
    this.queryFilter$ = this.dateAppointmentService.queryFilter$;
    this.currentUser$ = this.authenticationSelectors.selectCurrentUser();

    this.subscriptions.add(
      this.queryFilter$
        .pipe(
          filter((queryFilter) => queryFilter !== undefined),
          filter((queryFilter) => queryFilter.date !== null)
        )
        .subscribe((queryFilter) => {
          this.fromDate =
            sessionStorage.getItem("appointmentByDate") !== undefined &&
            sessionStorage.getItem("appointmentByDate") !== null
              ? sessionStorage.getItem("appointmentByDate")
              : queryFilter.date;
        })
    );
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.currentUser$
        .pipe(filter((currentUser) => currentUser !== undefined))
        .subscribe((currentUser) => {
          this.store.dispatch(PreviewEditTemplateActions.getLocations({userId: currentUser.userId}));

          // this.appointmentListService.getAppointmentSettings(currentUser.userId);
          this.store.dispatch(
            AppointmentSettingsActions.getAppointmentSettings({
              userId: currentUser.userId,
            })
          );

          this.subscriptions.add(
            this.strategyService.loaded$
              .pipe(filter((loaded) => !loaded))
              .subscribe((loaded) => this.strategyService.getAll())
          );
          this.dateAppointmentService.clearCache();
        })
    );

    this.subscriptions.add(
      this.activatedRoute.queryParamMap.subscribe((params) => {
        if (this.firstRequest) {
          if (
            params.keys.findIndex((key) => key === "remoteappointmentid") > -1
          ) {
            this.selectedRemoteAppointmentId = params.get(
              "remoteappointmentid"
            );

            this.dateAppointmentService.clearCache();
            const filters: QueryParams = {
              requestType: "remoteAppointment",
              remoteAppointmentId: this.selectedRemoteAppointmentId,
              offset: "0",
            };

            this.dateAppointmentService.subscribeWebSocketJob(filters);
          } else if (
            params.keys.findIndex((key) => key === "appointmentid") > -1
          ) {
            this.selectedAppointmentId = params.get("appointmentid");

            this.dateAppointmentService.clearCache();
            const filters: QueryParams = {
              requestType: "getAppointmentsOnDateByAppointmentId",
              appointmentId: this.selectedAppointmentId,
              offset: "0",
            };

            this.dateAppointmentService.subscribeWebSocketJob(filters);
          } else {
            this.dateAppointmentService.clearCache();
            this.getAppointments(this.fromDate, "0", "subscribe");
          }
        }
      })
    );

    this.firstRequest = false;

    this.viewModel$ = combineLatest([
      this.loading$,
      this.dateAppointments$,
    ]).pipe(
      map(([loading, dateAppointments]) => ({
        loading,
        dateAppointments,
      }))
    );

    this.appointmentListService.subscribeWebSocketJob({ personIds: [] });
    this.store.dispatch(AppointmentListActions.setPageType({payload: 'DateAppointment'}));
  }

  getAppointments(date, offset, type): void {
    this.dateAppointmentService.clearCache();
    const reminderfilters: QueryParams = {
      appointmentId: "0",
      viewSessionId: this.viewSessionId,
    };
    this.remindersReportService.refreshWebSocketJob(reminderfilters);

    const appointmentIdIndex = this.location.path().indexOf("appointmentid");
    if (appointmentIdIndex !== -1) {
      this.selectedAppointmentId = this.location
        .path()
        .substring(appointmentIdIndex + 14);
    }

    const mySimpleFormat = this.pipe.transform(date, "yyyy-MM-dd");
    const filters: QueryParams = {
      requestType: "appointmentByDate_refresh",
      date: mySimpleFormat.toString(),
      offset: offset.toString(),
    };

    sessionStorage.setItem("appointmentByDate", mySimpleFormat.toString());

    if (type === "refresh") {
      this.dateAppointmentService.refreshWebSocketJob(filters);
    } else {
      this.dateAppointmentService.subscribeWebSocketJob(filters);
    }
  }

  openSettings(): void {
    this.store.dispatch(AppointmentsActions.openAppointmentsSettings());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.dateAppointmentService.unsubscribeWebSocketJob();
    this.appointmentListService.unsubscribeWebSocketJob();
    this.remindersReportService.unsubscribeWebSocketJob();
  }

  getMoreAppointmentsOnScroll(totalLoaded): void {
    const fromFormat = this.pipe.transform(this.fromDate, "yyyy-MM-dd");
    const filters = {
      requestType: "appointmentByDate",
      date: fromFormat.toString(),
      offset: totalLoaded.toString(),
    };
    this.dateAppointmentService.refreshWebSocketJob(filters);
    sessionStorage.setItem("appointmentByDate", fromFormat.toString());
  }
}
